/**
 * ARTWORK STUDIO — THE WHOLE SUBTREE, NOT JUST THE TOP FOLDER (pure, zero imports).
 *
 * THE BUG THIS EXISTS TO KILL. `collectFolder` (canvaPaging.js) walks ONE
 * folder's continuation token to exhaustion — and it does that correctly. But
 * "Bezel & Top Designs" does not keep its 300+ bezels in that folder. It keeps
 * a handful there and the rest in SPORT SUB-FOLDERS (Football, Basketball,
 * Baseball, Wrestling, …). So a picker that pages one folder perfectly still
 * showed the operator a dozen designs and a row of folder tiles, and every
 * previous fix — the continuation loop, the folder-vs-design classifier, the
 * backend's `image` item type — made that dozen *correct* without making it
 * *complete*. Paging is depth. This is BREADTH, and it is the half that was
 * never built.
 *
 * `listFolder` on the backend is non-recursive by construction (it is one Canva
 * `/folders/{id}/items` call), and the backend's scoped search only runs on a
 * NON-EMPTY query and hard-caps at 240 items — below the real folder count. So
 * neither route can answer "every bezel" on its own. The recursion belongs
 * here, over the route that already pages correctly.
 *
 * PURE, and it takes the per-folder fetcher as an ARGUMENT — no `lib/api.js`,
 * so no `import.meta`, so `verify-studio.mjs` drives this exact loop under Node
 * against a stub feed shaped like the real one.
 *
 * Properties the walk guarantees:
 *   - BREADTH-FIRST from the folder the picker opened on, so the operator's own
 *     folder and its immediate children land first and the deep corners last;
 *   - every item is stamped with the folder it came from (`folderId`,
 *     `folderName`, `folderPath`), because a flattened grid of 300 tiles is
 *     useless if you cannot tell a football top from a wrestling one;
 *   - ids are deduped GLOBALLY, so a design that appears in two folders renders
 *     once;
 *   - a folder that fails is skipped and NAMED, never fatal — losing the other
 *     eleven sport folders to one bad one is the failure mode this replaces;
 *   - progress is emitted after every page of every folder, so 300+ designs
 *     fill the grid as they arrive instead of after the last request;
 *   - the caps are REPORTED, never silent: `truncated` carries what was left.
 */

/** How many folders one walk will visit. The engine tree is ~30; 80 is slack. */
export const TREE_FOLDER_CAP = 80

/** How many designs one walk will collect before handing back the wheel. */
export const TREE_ITEM_CAP = 3000

/**
 * Walk a folder AND EVERY FOLDER UNDER IT.
 *
 * @param {(folderId: string, opts: { onPage?: (node) => void }) => Promise<object>} fetchFolder
 *        Fully pages ONE folder and resolves its merged node:
 *        `{ id, name, folders, items, continuation, source, partial, reason }`.
 * @param {{ id: string, name?: string }} root  the folder the picker opened on
 * @param {{ maxFolders?: number, maxItems?: number, onProgress?: (node) => void,
 *           signal?: AbortSignal }} opts
 * @returns {Promise<object>} one node carrying every descendant design.
 */
export async function collectTree(fetchFolder, root = {}, opts = {}) {
  const {
    maxFolders = TREE_FOLDER_CAP,
    maxItems = TREE_ITEM_CAP,
    onProgress,
    signal,
  } = opts

  const rootId = root.id
  const rootName = root.name || 'Folder'

  const items = []
  const itemIds = new Set()
  const directFolders = []
  const directIds = new Set()
  const perFolder = []
  const queued = new Set([rootId])
  const queue = [{ id: rootId, name: rootName, path: [] }]

  let scanned = 0
  let source = null
  let partial = false
  let truncated = false
  let reason = null
  const failedFolders = []

  while (queue.length) {
    if (signal?.aborted) break

    if (scanned >= maxFolders) {
      truncated = true
      reason = `Stopped after ${scanned} folders — ${queue.length} more under ${rootName} were not opened. Drill into them directly, or narrow with the search box.`
      break
    }
    if (items.length >= maxItems) {
      truncated = true
      reason = `Stopped at ${items.length} designs — ${queue.length} folder${queue.length === 1 ? '' : 's'} under ${rootName} were not opened. Narrow with the search box.`
      break
    }

    const folder = queue.shift()
    scanned += 1

    // A page snapshot from `collectFolder` is CUMULATIVE for its folder, so
    // absorbing every one of them is safe — the id sets make it idempotent.
    const absorb = (node) => {
      if (!node || typeof node !== 'object') return
      if (!source && node.source) source = node.source
      if (node.partial) {
        partial = true
        if (!reason) reason = node.reason || `The feed stopped part-way through ${folder.name}.`
      }
      for (const sub of node.folders || []) {
        if (!sub || !sub.id || queued.has(sub.id)) continue
        queued.add(sub.id)
        queue.push({ id: sub.id, name: sub.name || 'Folder', path: [...folder.path, folder.name] })
        // The picker still lists the folder it opened on as drillable tiles, so
        // "this folder only" and the breadcrumb keep working underneath.
        if (folder.id === rootId && !directIds.has(sub.id)) {
          directIds.add(sub.id)
          directFolders.push({ id: sub.id, name: sub.name || 'Folder' })
        }
      }
      for (const it of node.items || []) {
        const key = it && it.id != null ? String(it.id) : null
        if (!key || itemIds.has(key)) continue
        itemIds.add(key)
        items.push({
          ...it,
          folderId: folder.id,
          folderName: folder.name,
          // The trail BELOW the folder that was opened, so a tile reads
          // "Football" rather than "Bezel & Top Designs / Football".
          folderPath: [...folder.path.slice(1), folder.name].join(' / ') || folder.name,
        })
      }
    }

    let node = null
    try {
      node = await fetchFolder(folder.id, {
        onPage: (page) => {
          absorb(page)
          if (onProgress) onProgress(snapshot(true))
        },
      })
    } catch (err) {
      if (err?.name === 'AbortError') throw err
      // One bad folder must not cost the other eleven. Name it and carry on.
      failedFolders.push(folder.name)
      partial = true
      reason = `Could not read ${failedFolders.join(', ')} — every other folder under ${rootName} did load.`
      if (onProgress) onProgress(snapshot(true))
      continue
    }

    absorb(node)
    perFolder.push({ id: folder.id, name: folder.name, count: countFrom(folder.id) })
    if (onProgress) onProgress(snapshot(queue.length > 0))
  }

  return snapshot(false)

  function countFrom(id) {
    let n = 0
    for (const it of items) if (it.folderId === id) n += 1
    return n
  }

  function snapshot(loading) {
    return {
      id: rootId,
      name: rootName,
      folders: directFolders,
      items,
      // Deep mode has no single resumable cursor — every folder was walked to
      // exhaustion — so there is no "Load more" to offer, and saying so beats
      // handing back a token that means nothing.
      continuation: null,
      source,
      partial,
      truncated,
      reason,
      deep: true,
      scanned,
      pending: queue.length,
      tree: perFolder,
      loading,
    }
  }
}

/**
 * Filter a collected tree by name — CLIENT-SIDE, over everything already
 * loaded.
 *
 * The backend's scoped search (`canva.js#searchInFolder`) does walk the subtree,
 * but it caps at 240 items and returns `continuation: null`, so on a 300+ folder
 * it silently answers with less than the folder holds and offers no way to ask
 * for the rest. Once the tree is in memory the honest search is this one: it
 * sees every design the walk collected, and it matches the FOLDER name too, so
 * typing "football" finds the football sub-folder's designs whatever they are
 * individually called.
 */
export function filterTree(items, q) {
  const arr = Array.isArray(items) ? items : []
  const needle = String(q || '').trim().toLowerCase()
  if (!needle) return arr
  return arr.filter((it) => {
    const name = String(it?.name || '').toLowerCase()
    const path = String(it?.folderPath || it?.folderName || '').toLowerCase()
    return name.includes(needle) || path.includes(needle)
  })
}
